import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import _ from 'lodash';

Vue.use(Vuex);
axios.defaults.baseURL = process.env.VUE_APP_API_URL;

const user = JSON.parse(localStorage.getItem('dmCMS.user'));
const todaysDate = new Date();
const year = todaysDate.getFullYear().toString();
const month = (todaysDate.getMonth() + 1).toString();
const day = todaysDate.getDate().toString();
const formattedDate = `${year}-${month}-${day}`;

if (user) {
  axios.defaults.baseURL = `${process.env.VUE_APP_API_URL}?userId=${user.userId}&projectName=${user.projectName}&authToken=${user.authToken}`;
}

const makeRequest = (params, data) => axios({
  method: data ? 'post' : 'get',
  params,
  data,
}).then(({ data }) => {
  if (data.errors) {
    let title = 'The following errors occurred';
    let msg = _.map(
      data.errors,
      (e) => `&bull; ${e.message}`,
    ).join('<br>');

    if (msg.includes('Authentication failed.') || msg.includes('Session has expired.')) {
      title = 'Please sign in again';
      msg = 'Your session has expired.';
      store.commit('signOut');
      store.commit('authError', store.$app.$route);
      store.$app.$router.push({ name: 'SignIn' });
    }

    store.$app.$emit('show-warning', {
      title,
      msg,
    });

    return Promise.reject(data);
  }

  return data;
});

const store = new Vuex.Store({
  state: {
    loadingMessage: '',
    showGlobalOverlay: false,
    user,
    selectedContentFilter: '',
    content: [],
    contentItem: {},
    contentItemRevisions: [],
    blogs: [],
    blogItem: {
      hidden: false,
      date: formattedDate,
    },
    blogImageDimensions: {
      blogImageWidth: 0,
      blogImageHeight: 0,
    },
    blogItemRevisions: [],
    files: [],
    cropImageInformation: {
      imageMode: 'view',
      imageSrc: '',
      xOffset: 0,
      yOffset: 0,
      width: 0,
      height: 0,
    },
    projectSettings: {
      // previewURL: '',
      // publishURL: '',
    },
  },
  getters: {
    isLoggedIn: (state) => !!state.user && !state.loadingMessage.includes('Authorizing'),
    isLoadingActive: (state) => !!state.loadingMessage,
    isGlobalOverlayActive: (state) => !!state.showGlobalOverlay,
    cropBoxDimensions: (state) => {
      const cBoxWidth = (state.cropImageInformation.width);
      const cBoxHeight = (state.cropImageInformation.height);
      return `${cBoxWidth}w x ${cBoxHeight}h`;
    },
  },
  mutations: {
    /* Loading / Busy */
    showLoader(state, data) {
      state.loadingMessage = data.loadingMessage;
      state.showGlobalOverlay = data.showGlobalOverlay;
    },
    hideLoader(state) {
      state.loadingMessage = '';
      state.showGlobalOverlay = false;
    },
    /* / Loading / Busy */

    /* Init App Data */
    initialize(state, data) {
      [
        state.content,
        state.contentItem,
        state.blogs,
        state.blogItem = {
          hidden: false,
          date: formattedDate,
        },
        state.files,
        state.cropImageInformation = {
          imageMode: 'view',
          imageSrc: '',
          xOffset: 0,
          yOffset: 0,
          width: 0,
          height: 0,
        },
      ] = data;
    },
    /* / Init App */

    /* Auth */
    authSuccess(state, user) {
      state.user = user;
    },
    authError(state, route) {
      const referrer = {
        name: route.name,
        params: _.clone(route.params),
      };

      state.referrer = referrer;
    },
    clearAuthError(state) {
      state.referrer = null;
    },
    signOut(state) {
      state.loadingMessage = '';
      state.showGlobalOverlay = false;
      state.user = {};
      state.content = [];
      state.contentItem = {};
      state.contentItemRevisions = [];
      state.blogs = [];
      state.blogItem = {};
      state.blogItemRevisions = [];
      state.files = [];
      state.cropImageInformation = {};
      axios.defaults.baseURL = process.env.VUE_APP_API_URL;
      localStorage.clear();
    },
    /* / Auth */

    /* Content */
    setSelectedContentFilter(state, filter) {
      state.selectedContentFilter = filter;
    },
    setContent(state, content) {
      state.content = content;
    },
    setContentItem(state, contentItem) {
      state.contentItem = contentItem;
    },
    setContentItemById(state, contentId) {
      const contentItem = _.find(state.content, { contentId }) || {};
      state.contentItem = _.clone(contentItem);
    },
    setContentItemRevisions(state, revisions) {
      state.contentItemRevisions = revisions;
    },
    setContentItemImageSrc(state, imageSrc) {
      state.contentItem.content = imageSrc;
    },
    /* / Content */

    /* Blog */
    setBlogs(state, blogs) {
      state.blogs = blogs;
    },
    setBlogItem(state, blogItem) {
      state.blogItem = blogItem;
    },
    setBlogItemById(state, postId) {
      const blogItem = _.find(state.blogs, { postId }) || {};
      state.blogItem = _.clone(blogItem);
    },
    setBlogItemRevisions(state, revisions) {
      state.blogItemRevisions = revisions;
    },
    setBlogItemImageSrc(state, imageSrc) {
      state.blogItem.imageContent = imageSrc;
    },
    setBlogImageDimensions(state, blogImageDimensions) {
      state.blogImageDimensions = blogImageDimensions;
    },
    /* / Blog */

    /* Image Cropping */
    setCropImageInformation(state, cropImageInformation) {
      state.cropImageInformation = cropImageInformation;
    },
    setCropImageTempSrc(state, payload) {
      state.cropImageInformation.imageSrc = payload.imageSrc;
      state.cropImageInformation.imageMode = payload.imageMode;
    },
    /* / Image Cropping */

    /* Project Settings Panel */
    setProjectSettings(state, projectSettings) {
      state.projectSettings = projectSettings;
    },
    /* / Project Settings Panel */
  },
  actions: {
    /* Init App */
    initialize({ commit, state, dispatch }) {
      let initPromise;

      if (state.user) {
        commit('showLoader', {
          loadingMessage: 'Loading data...',
          showGlobalOverlay: true,
        });

        initPromise = makeRequest({
          action: 'itemsGet',
        }).then((content) => {
          const { contentId, blogId } = store.$app.$route.params;
          let contentItem = {};

          if (contentId) {
            contentItem = _.clone(_.find(content, { contentId }));
          }

          const blogsPromise = makeRequest({
            action: 'blogsGet',
          });

          const filesPromise = makeRequest({
            action: 'userFilesGetAll',
          });

          const projectSettingsPromise = dispatch('getProjectSettings');

          return Promise.all([
            blogsPromise,
            filesPromise,
            projectSettingsPromise,
          ]).then((data) => {
            const [blogs, files] = data;
            let blogItem = {
              hidden: false,
              date: formattedDate,
            };
            if (blogId) {
              blogItem = _.clone(_.find(blogs, { postId: blogId }));
            }

            commit('initialize', [content, contentItem, blogs, blogItem, files]);
          });
        }).finally(() => {
          commit('hideLoader');
        });
      } else {
        initPromise = Promise.reject(new Error('User is not signed in.'));
      }

      return initPromise;
    },
    /* / Init App */

    /* Auth */
    signIn({ commit }, credentials) {
      const params = _.clone(credentials);

      params.action = 'signIn';

      commit('showLoader', {
        loadingMessage: 'Authorizing...',
      });

      return makeRequest(params)
        .then(({ data }) => {
          const user = {
            projectName: credentials.projectName,
            userId: credentials.userId,
            authToken: data.message,
          };

          axios.defaults.baseURL = `${process.env.VUE_APP_API_URL}?userId=${user.userId}&projectName=${user.projectName}&authToken=${user.authToken}`;

          localStorage.setItem('dmCMS.user', JSON.stringify(user));

          commit('authSuccess', user);

          return user;
        })
        .catch(() => {
          localStorage.removeItem('dmCMS.user');
          return Promise.reject();
        })
        .finally(() => {
          commit('hideLoader');
        });
    },
    signOut({ commit }) {
      const promise = makeRequest({ action: 'signOut' });
      commit('signOut');
      return promise;
    },
    /* / Auth */

    /* Content */
    getContentItem({ commit }, contentId) {
      commit('showLoader', {
        loadingMessage: 'Loading data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'itemGet',
        contentId,
      }).then((content) => {
        commit('setContentItem', content.data);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    getContentItemRevisions({ commit }, contentId) {
      commit('showLoader', {
        loadingMessage: 'Loading data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'itemGetBackups',
        contentId,
      }).then((revisions) => {
        commit('setContentItemRevisions', revisions);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    updateContentItem({ commit, state }, action) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action,
      }, state.contentItem)
        .then(() => makeRequest({
          action: 'itemsGet',
        })).then((content) => {
          commit('setContent', content);
          commit('setContentItem', {});
          commit('setContentItemRevisions', []);
        }).finally(() => {
          commit('hideLoader');
        });
    },
    updateActiveContentItemVersion({ commit }, content) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'itemSetActiveVersion',
        contentId: content.contentId,
        contentVersionNumber: content.version,
      }).then(() => makeRequest({
        action: 'itemsGet',
      })).then((content) => {
        commit('setContent', content);
        commit('setContentItem', {});
        commit('setContentItemRevisions', []);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    setDefaultContentItem({ commit }) {
      const contentItem = {};
      commit('setContentItem', contentItem);
    },
    setContentItemImageSrc({ state, commit }) {
      commit('setContentItemImageSrc', state.cropImageInformation.imageSrc);
    },
    /* / Content */

    /* Blog */
    getBlogItemRevisions({ commit }, postId) {
      commit('showLoader', {
        loadingMessage: 'Loading data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'blogGetBackups',
        postId,
      }).then((revisions) => {
        commit('setBlogItemRevisions', revisions);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    getBlogImageDimensions({ commit }) {
      return makeRequest({
        action: 'blogImageDimensionsGet',
      }).then((response) => {
        commit('setBlogImageDimensions', response.data);
      });
    },
    createBlogItem({ commit, state }) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      const data = _.cloneDeep(state.blogItem);

      return makeRequest({
        action: 'blogCreate',
      }, data).then(() => makeRequest({
        action: 'blogsGet',
      })).then((blogs) => {
        commit('setBlogs', blogs);
        commit('setBlogItem', {});
      }).finally(() => {
        commit('hideLoader');
      });
    },
    updateBlogItem({ commit, state }) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      const data = _.cloneDeep(state.blogItem);

      return makeRequest({
        action: 'blogUpdate',
      }, data).then(() => makeRequest({
        action: 'blogsGet',
      })).then((blogs) => {
        commit('setBlogs', blogs);
        commit('setBlogItem', {});
        commit('setBlogItemRevisions', []);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    updateActiveBlogItemVersion({ commit }, blog) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'blogSetActiveVersion',
        postId: blog.postId,
        contentVersionNumber: blog.version,
      }).then(() => makeRequest({
        action: 'blogsGet',
      })).then((blogs) => {
        commit('setBlogs', blogs);
        commit('setBlogItem', {});
        commit('setBlogItemRevisions', []);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    setDefaultBlogItem({ commit }) {
      const blogItem = { date: formattedDate, hidden: false, content: '' };
      commit('setBlogItem', blogItem);
    },
    setBlogItemImageSrc({ state, commit }) {
      commit('setBlogItemImageSrc', state.cropImageInformation.imageSrc);
    },
    /* / Blog */

    /* Files */
    uploadFile({ state, commit }, payload = {}) {
      commit('showLoader', {
        loadingMessage: 'Uploading file...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'userFileSave',
        userFileName: payload.file.name,
      }, payload.file).then(() => makeRequest({
        action: 'userFilesGetAll',
      })).then((files) => {
        state.files = files;
      }).finally(() => {
        commit('hideLoader');
      });
    },
    deleteFile({ state, commit }, payload = {}) {
      commit('showLoader', {
        loadingMessage: 'Deleting file...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'userFileDelete',
        userFileName: payload.fileName,
      }).then(() => makeRequest({
        action: 'userFilesGetAll',
      })).then((files) => {
        state.files = files;
      }).finally(() => {
        commit('hideLoader');
      });
    },
    /* / Files */

    /* Image Cropping */
    setCropImageTempSrc({ commit }, payload) {
      const newCropImageInformation = {};
      newCropImageInformation.imageSrc = payload.imageSrc;
      newCropImageInformation.imageMode = payload.imageMode;
      newCropImageInformation.xOffset = 0;
      newCropImageInformation.yOffset = 0;
      newCropImageInformation.width = 0;
      newCropImageInformation.height = 0;
      commit('setCropImageInformation', newCropImageInformation);
    },
    clearCropImageTempSrc({ commit }) {
      const defaultCropImageInformation = {
        imageSrc: '',
        imageMode: 'view',
        xOffset: 0,
        yOffset: 0,
        width: 0,
        height: 0,
      };
      commit('setCropImageInformation', defaultCropImageInformation);
    },
    setCroppedBoundaries({ state, commit }, croppedBoundaryObj) {
      const xOffset = Math.round(croppedBoundaryObj.xOffset);
      const yOffset = Math.round(croppedBoundaryObj.yOffset);
      const width = Math.round(croppedBoundaryObj.width);
      const height = Math.round(croppedBoundaryObj.height);

      const newCroppedImageState = state.cropImageInformation;
      newCroppedImageState.xOffset = xOffset;
      newCroppedImageState.yOffset = yOffset;
      newCroppedImageState.width = width;
      newCroppedImageState.height = height;

      commit('setCropImageInformation', newCroppedImageState);
    },
    cropImage({ state, commit }) {
      commit('showLoader', {
        loadingMessage: 'Cropping Image...',
        showGlobalOverlay: true,
      });

      const data = _.cloneDeep(state.cropImageInformation);

      return makeRequest({
        action: 'cropImage',
      }, data).then((response) => {
        const payload = {};
        payload.imageSrc = response.data.message;
        payload.imageMode = 'view';
        commit('setCropImageTempSrc', payload);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    /* / Image Cropping */

    /* Preview / Publish */
    preview({ commit }, generatePlaceholders) {
      commit('showLoader', {
        loadingMessage: 'Previewing site...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'previewSite',
        placeHolderImages: generatePlaceholders,
      }).finally(() => {
        commit('hideLoader');
      });
    },
    publish({ commit }) {
      commit('showLoader', {
        loadingMessage: 'Publishing site...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'publishSite',
      }).finally(() => {
        commit('hideLoader');
      });
    },
    /* / Preview / Publish */

    /* Project Config Panel */
    getProjectSettings({ commit }) {
      commit('showLoader', {
        loadingMessage: 'Getting project settings...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'projectSettingsGet',
      }).then((response) => {
        commit('setProjectSettings', response);

        return response.data;
      }).finally(() => {
        commit('hideLoader');
      });
    },
    /* / Project Config Panel */
  },
});

export default store;
